import { useMutation } from "@tanstack/react-query";
import { useRecoilState } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useNavigate } from "react-router-dom";
import {
  axiosInstancePromotion,
  CustomResponseFormat,
  setBearerTokenPromotion,
} from "api/instancePromotion";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import mixpanel from "mixpanel-browser";
import useMixpanelProfileMkt from "./useMixpanelProfileMkt";
import useMixpanelMKT from "./useMixpanelMKT";
import useGtagConversion from "./useGtagConversion";
import { useState } from "react";
import { webFunnelApi } from "../api/webFunnelApi";
import { captureException } from "../utils/sentry";
import { PATH } from "../routes/paths";

type SocialType = "google" | "apple";

interface LoginVars {
  idToken: string;
  email?: string;
}

export default function useLoginPromotionMutation(type: SocialType) {
  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userSimpleAtom);
  const setPromotionPeople = useMixpanelProfileMkt("v4");
  const trackGtagEvent = useGtagConversion();
  const trackMixpanelRegistration = useMixpanelMKT({
    eventName: "가입",
    version: "v4",
  });
  const trackMixpanelLogin = useMixpanelMKT({
    eventName: "로그인",
    version: "v4",
  });
  const [error, setError] = useState<string>();

  const { mutate: identify } = useMutation(
    ({ distinctId }: { distinctId: string }) =>
      webFunnelApi.identify(distinctId),
    {
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_identify" },
          extra: { distinctId: variables.distinctId },
        });
      },
    }
  );

  const mutation = useMutation({
    mutationFn: async ({ idToken, email }: LoginVars) => {
      const response = await axiosInstancePromotion.post<CustomResponseFormat>(
        `/user/v2/login`,
        {
          idToken,
          type,
          ...(email && { email }),
        }
      );
      return response;
    },
    onSuccess: (response) => {
      if (response.code === 0) {
        const result: any = response.result;

        setBearerTokenPromotion(result.accessToken);
        setUser(result);

        const distinctId =
          localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
          (mixpanel as any).promotion?.get_distinct_id();

        if (distinctId) {
          identify({ distinctId: distinctId });
        }

        if (result.isFirstLogin) {
          // Meta CAPI
          trackCapiStandardEvent("CompleteRegistration", {
            value: 0,
            currency: "KRW",
            externalId: result.userId,
            email: result.email,
          });

          // Google Ads
          trackGtagEvent("CompleteRegistration", "v4");

          // Mixpanel identify & people
          trackMixpanelRegistration();
        } else {
          trackMixpanelLogin();
        }

        // Mixpanel identify & people
        const promotionInstance = (mixpanel as any).promotion;
        if (promotionInstance && result.trackId) {
          // promotionInstance.identify(result.trackId);

          setPromotionPeople({
            $email: result.email,
            $user_id: result.userId,
            ...result.nickname && { $name: result.nickname },
          });
        }

        if (result.isFirstLogin) {
          navigate("/promotion-onboarding/nickname/1", { replace: true });
        } else {
          if (result?.hasAlreadyUsed) {
            navigate(PATH.promotionAlreadyUsed, { replace: true });
          } else {
            navigate("/promotion-onboarding/auth-loading/1", { replace: true });
          }
        }
      } else {
        setError(`로그인에 실패했습니다. 다시 시도해주세요.: ${response.code}`);
      }
    },
    onError: (e: any) => {
      console.error("Promotion login failed:", e);
      setError(`로그인에 실패했습니다. 다시 시도해주세요.`);
    },
  });

  return {
    login: mutation.mutate,
    loginAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error,
  };
}
